import { Hourglass, CheckCircle, Clock, ExternalLink, RefreshCw, AlertCircle } from 'lucide-react';
import { ProjectData } from '../types';
import { resolveSupportContact, supportPhoneTelHref } from '../utils/supportContact';

interface AppStoreApprovalWaitingProps {
  project: ProjectData;
}

export const AppStoreApprovalWaiting = ({ project }: AppStoreApprovalWaitingProps) => {
  const { appType, jiraKey } = project;
  const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
  const support = resolveSupportContact(appType, project.supportContact);

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              🎉 App Successfully Submitted!
            </h3>
            <p className="text-green-700 mb-4">
              Excellent! Your {appName} app has been successfully submitted to the App Store and is now under review by Apple and/or Google.
            </p>
            <div className="bg-white border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800 font-medium mb-2">✅ Submission Complete</p>
              <p className="text-sm text-green-700">
                Your app is now in the hands of the App Store review teams.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting Status */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Hourglass className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-800 mb-2">
              ⏳ Currently Under Review
            </h3>
            <p className="text-amber-700 mb-4">
              Your app is now in the review queue. We'll notify you as soon as it's approved and live!
            </p>
            <div className="bg-white border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">🕐 Review in Progress</p>
              <p className="text-sm text-amber-700">
                This is the final step before your app goes live in the App Store.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Review Status Tracking */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📊 Review Status Tracking
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center">
              <CheckCircle className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">App Submission Complete</h3>
              <p className="text-gray-600 text-sm">Your app has been successfully uploaded to the App Store</p>
            </div>
            <span className="text-green-600 text-sm font-medium">Complete</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-amber-100 text-amber-800 rounded-full w-8 h-8 flex items-center justify-center">
              <RefreshCw className="h-4 w-4 animate-spin" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Under Review by Apple</h3>
              <p className="text-gray-600 text-sm">Apple's review team is examining your app for compliance and quality</p>
            </div>
            <span className="text-amber-600 text-sm font-medium">In Progress</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              <RefreshCw className="h-4 w-4 animate-spin" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Under Review by Google</h3>
              <p className="text-gray-600 text-sm">Google Play's review team is checking your app for policy compliance</p>
            </div>
            <span className="text-blue-600 text-sm font-medium">In Progress</span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="bg-gray-100 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">App Goes Live</h3>
              <p className="text-gray-600 text-sm">Once approved, your app will be immediately available for download</p>
            </div>
            <span className="text-gray-500 text-sm font-medium">Pending</span>
          </div>
        </div>
      </div>

      {/* Review Process Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">
          📋 What Happens During Review
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                1
              </div>
              <div>
                <p className="font-medium text-blue-900">Content Review</p>
                <p className="text-blue-700 text-sm">Apple/Google reviews your app's content and functionality</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                2
              </div>
              <div>
                <p className="font-medium text-blue-900">Compliance Check</p>
                <p className="text-blue-700 text-sm">Ensures your app follows all store guidelines and policies</p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                3
              </div>
              <div>
                <p className="font-medium text-blue-900">Technical Review</p>
                <p className="text-blue-700 text-sm">Verifies your app works properly and doesn't crash</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
                4
              </div>
              <div>
                <p className="font-medium text-blue-900">Security Scan</p>
                <p className="text-blue-700 text-sm">Checks for any security vulnerabilities or malicious code</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          ⏱️ Typical Review Timeline
        </h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              ✓
            </div>
            <div>
              <p className="font-medium text-gray-900">Submitted: Your app is now in the review queue</p>
              <p className="text-gray-600 text-sm">Review process has begun</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-amber-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              1-3
            </div>
            <div>
              <p className="font-medium text-gray-900">1-3 days: Apple review process</p>
              <p className="text-gray-600 text-sm">Typical timeframe for Apple App Store review</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              3-7
            </div>
            <div>
              <p className="font-medium text-gray-900">3-7 days: Google Play review completion</p>
              <p className="text-gray-600 text-sm">Typical timeframe for Google Play Store review</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold">
              🚀
            </div>
            <div>
              <p className="font-medium text-gray-900">Upon approval: App goes live immediately</p>
              <p className="text-gray-600 text-sm">Your app will be available for download right away</p>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Review times can vary depending on app complexity and current store volume. Most apps are approved within 1-7 days, but complex apps may take longer.
            </p>
          </div>
        </div>
      </div>

      {/* Help Resources */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📚 Help Resources
        </h3>
        <div className="space-y-3">
          <a
            href={`/project/${jiraKey}/help#app-store-review`}
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              📖
            </div>
            <div>
              <p className="font-medium text-gray-900">App Store Review Guide</p>
              <p className="text-sm text-gray-600">Learn more about the review process and what to expect</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="https://developer.apple.com/app-store/review/guidelines/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              🍎
            </div>
            <div>
              <p className="font-medium text-gray-900">Apple Review Guidelines</p>
              <p className="text-sm text-gray-600">Official Apple guidelines for app approval</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="https://support.google.com/googleplay/android-developer/answer/9859348"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
          >
            <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
              🤖
            </div>
            <div>
              <p className="font-medium text-gray-900">Google Play Review Process</p>
              <p className="text-sm text-gray-600">Official Google Play review information</p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
          </a>
        </div>
      </div>

      {/* Support Contact */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-amber-900 mb-3">
          🆘 Questions About the Review Process?
        </h3>
        <p className="text-amber-800 mb-4">
          Our support team is here to help during the review process:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-amber-100 text-amber-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-amber-900">Email Support</p>
              <p className="text-sm text-amber-700">
                <a href={`mailto:${support.email}`} className="hover:text-amber-900">
                  {support.email}
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-amber-100 text-amber-800 rounded-full w-8 h-8 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-amber-900">Phone Support</p>
              <p className="text-sm text-amber-700">
                <a href={supportPhoneTelHref(support.phone)} className="hover:text-amber-900">
                  {support.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          🎯 What Happens Next?
        </h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              1
            </div>
            <div>
              <p className="font-medium text-gray-900">We monitor the review process</p>
              <p className="text-gray-600 text-sm">Our team tracks the progress and status of your app review</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              2
            </div>
            <div>
              <p className="font-medium text-gray-900">You get notified of approval</p>
              <p className="text-gray-600 text-sm">We'll notify you immediately when your app is approved and live</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              3
            </div>
            <div>
              <p className="font-medium text-gray-900">Your app goes live!</p>
              <p className="text-gray-600 text-sm">Your {appName} app will be available for download in the App Store</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold mt-0.5">
              4
            </div>
            <div>
              <p className="font-medium text-gray-900">Project completion</p>
              <p className="text-gray-600 text-sm">Your project will be marked as complete and deployed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
